import Icon from '@ant-design/icons';
import { Layout } from 'antd';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Cell, Grid } from 'styled-css-grid';
import { useWindowSize } from '../Hooks/windowSize';
import enResource from '../locales/en/Resources.json';
import fiResource from '../locales/fi/Resources.json';
import plResource from '../locales/pl/Resources.json';
import svResource from '../locales/sv/Resources.json';
import { SidebarIcon } from './Menus/icons/SidebarIcon';
import { SidebarLogo } from './SidebarLogo';

const { Sider } = Layout;

const StyledSider = styled(Sider)`
  overflow-y: auto;
  overflow-x: hidden;
  &&& {
    position: unset;
    background: #0f273e;
    height: 100vh;
    ::-webkit-scrollbar {
      display: ${(props) => (props.collapsed ? 'none' : 'block')};
    }
    ::-webkit-scrollbar-thumb {
      box-shadow: inset 0 0 7px 7px rgba(255, 255, 255, 0.5);
    }
  }
  &&& .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
`;

const StyledCell = styled(Cell)`
  display: flex;
  justify-content: left;
`;

const StyledSidebarIconWrapper = styled.div`
  margin-left: -1.4em;
  margin-bottom: 1.5em;
  padding: 0;
`;

const StyledFullSidebarIcon = styled(Icon)`
  font-size: 7em;
  color: #707689;
`;

interface IMenu {
  hasAccess: boolean;
  menuComponent: React.ElementType;
  props?: any;
}

export interface ISidebarProps {
  localStorageKey: string;
  title: string;
  subTitle: string;
  collapsedTitle: string;
  firstMenu: Array<IMenu>;
  secondMenu: Array<IMenu>;
}

export const Sidebar = (props: ISidebarProps) => {
  const i18n = useTranslation()[1];
  i18n.addResources('en', 'geosecma-sidebar', enResource.sidebar);
  i18n.addResources('sv', 'geosecma-sidebar', svResource.sidebar);
  i18n.addResources('fi', 'geosecma-sidebar', fiResource.sidebar);
  i18n.addResources('pl', 'geosecma-sidebar', plResource.sidebar);

  const windowSize = useWindowSize();

  const [expandDisabled, setExpandDisabled] = React.useState(windowSize.width! < 1300);
  const [collapsed, setCollapsed] = React.useState(localStorage.getItem(props.localStorageKey) === 'true');

  React.useEffect(() => {
    if (windowSize.width! < 1300 && !expandDisabled) {
      setExpandDisabled(true);
    } else if (windowSize.width! >= 1300 && expandDisabled) {
      setExpandDisabled(false);
    }
  }, [windowSize, expandDisabled]);

  const collapse = (collapsed: boolean) => {
    setCollapsed(collapsed);
    localStorage.setItem(props.localStorageKey, collapsed.toString());
    window.setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 300);
  };

  return (
    <StyledSider
      width={400}
      collapsedWidth={60}
      collapsible
      collapsed={collapsed || expandDisabled}
      onCollapse={(collapsed: boolean) => collapse(collapsed)}
      trigger={null}
    >
      <Grid style={{ height: '100%', maxHeight: '1000px' }} columns={1} rows="1fr 4fr 6fr" justifyContent="start">
        <Cell>
          <SidebarLogo
            title={props.title}
            subTitle={props.subTitle}
            collapsedTitle={props.collapsedTitle}
            collapsed={collapsed}
            onCollapse={() => collapse(!collapsed)}
          />
        </Cell>
        <Cell>
          {props.firstMenu
            .filter((menu) => menu.hasAccess)
            .map((menu, index) => (
              <menu.menuComponent key={`firstMenu-${index}`} {...menu.props} collapsed={collapsed.toString()} />
            ))}
        </Cell>
        <Cell>
          {props.secondMenu
            .filter((menu) => menu.hasAccess)
            .map((menu, index) => (
              <menu.menuComponent key={`secondMenu-${index}`} {...menu.props} collapsed={collapsed.toString()} />
            ))}
        </Cell>
        <StyledCell>
          <StyledSidebarIconWrapper>
            <StyledFullSidebarIcon component={SidebarIcon} title="GEOSECMA logo" />
          </StyledSidebarIconWrapper>
        </StyledCell>
      </Grid>
    </StyledSider>
  );
};
